import React, { useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import Upload from '@mui/icons-material/Upload';
import { Container, Grid } from '@mui/material';
import { useNavigate } from 'react-router';

import { ReadFile, TooltipIconButton, useHiddenFileSelect } from '@paragon/client-tools';
import { HangerList, LumberListPrototype, PlateList } from '@paragon/contract-library';
import {
  getClientUrls,
  hasCanUsePlatesPermission,
  hasMaterialsEditPermission,
  isLoggedInUserRoot,
  UpgradeNotification,
} from '@paragon/login-tools';
import { TypographyWithActionButtons } from '@paragon/material';
import { sortMaterialLists } from '@paragon/material-lists';
import { enqueueError } from '@paragon/notification-tools';

import { createHangerList, upsertHangerListPrototype } from '../../actions/hangerListActions';
import { createLumberListPrototype, upsertLumberListPrototype } from '../../actions/lumberListPrototypeActions';
import { createPlateList, upsertPlateListPrototype } from '../../actions/plateListActions';
import { getHangerLists } from '../../selectors/hangerListSelectors';
import { getLumberListPrototypes } from '../../selectors/lumberListPrototypeSelectors';
import { getPlateLists } from '../../selectors/plateListSelectors';
import { GRID_SPACING } from '../../util/constants';
import { useAppDispatch, useAppSelector } from '../hooks';
import { HangerListCard } from '../interface/cards/HangerListCard';
import { LumberListCard } from '../interface/cards/LumberListCard';
import { PlateListCard } from '../interface/cards/PlateListCard';
import { NameDialog } from '../interface/NameDialog';
import { LoadingPage } from './LoadingPage';

export function MainPage() {
  const lumberLists = useAppSelector(getLumberListPrototypes);
  const plateLists = useAppSelector(getPlateLists);
  const hangerLists = useAppSelector(getHangerLists);

  if (lumberLists === 'loading' || plateLists === 'loading' || hangerLists === 'loading') return <LoadingPage />;

  return <UnconnectedMainPage lumberLists={lumberLists} plateLists={plateLists} hangerLists={hangerLists} />;
}

export function UnconnectedMainPage(props: {
  readonly lumberLists: readonly LumberListPrototype[];
  readonly plateLists: readonly PlateList[];
  readonly hangerLists: readonly HangerList[];
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const hasEditPermission = useAppSelector(hasMaterialsEditPermission);
  const canUsePlates = useAppSelector(hasCanUsePlatesPermission);
  const isUserRoot = useAppSelector(isLoggedInUserRoot);
  const viewOnly = !useAppSelector(hasMaterialsEditPermission);
  const urls = useAppSelector(getClientUrls);

  const [renderFileSelect, selectFiles] = useHiddenFileSelect('.json', true);

  const processUploadedLumberListFile = async ({ contents, filename }: ReadFile) => {
    const lumberListPrototype = JSON.parse(atob(contents.split(',')[1]));
    try {
      await dispatch(upsertLumberListPrototype(lumberListPrototype));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Failed to import lumber list from ${filename}.`));
    }
  };

  const processUploadedPlateListFile = async ({ contents, filename }: ReadFile) => {
    const plateList = JSON.parse(atob(contents.split(',')[1]));
    try {
      await dispatch(upsertPlateListPrototype(plateList));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Failed to import plate list from ${filename}.`));
    }
  };

  const processUploadedHangerListFile = async ({ contents, filename }: ReadFile) => {
    const hangerList = JSON.parse(atob(contents.split(',')[1]));
    try {
      await dispatch(upsertHangerListPrototype(hangerList));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Failed to import hanger list from ${filename}.`));
    }
  };

  const sortedLumberLists = useMemo(() => sortMaterialLists(props.lumberLists), [props.lumberLists]);
  const sortedPlateLists = useMemo(() => sortMaterialLists(props.plateLists), [props.plateLists]);
  const sortedHangerLists = useMemo(() => sortMaterialLists(props.hangerLists), [props.hangerLists]);

  const [openAddDialog, setOpenAddDialog] = useState<null | 'plate list' | 'lumber list' | 'hanger list'>(null);

  return (
    <Container>
      <Grid container spacing={GRID_SPACING}>
        <Grid size={{ xs: 12 }}>
          <UpgradeNotification
            active={viewOnly}
            description="Upgrade to create or edit materials"
            trialDescription="Start your free trial to create or edit materials"
            onUpgradeClick={() => void window.open(`${urls.servicesUrl}account`, '_self')}
            sx={{ mb: 1 }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: canUsePlates ? 4 : 6 }}>
          <TypographyWithActionButtons text="Lumber Lists" variant="h4" gutterBottom>
            {hasEditPermission && (
              <TooltipIconButton title="Add new" onClick={() => setOpenAddDialog('lumber list')}>
                <AddIcon />
              </TooltipIconButton>
            )}
            {isUserRoot && (
              <TooltipIconButton
                title="Import"
                onClick={async () => {
                  const files = await selectFiles();
                  for (const file of files) await processUploadedLumberListFile(file);
                }}
              >
                <Upload />
              </TooltipIconButton>
            )}
          </TypographyWithActionButtons>

          {sortedLumberLists.map((list) => (
            <LumberListCard key={list.guid} value={list} />
          ))}
        </Grid>
        {canUsePlates && (
          <Grid size={{ xs: 12, md: 4 }}>
            <TypographyWithActionButtons text="Plate Lists" variant="h4" gutterBottom>
              {hasEditPermission && (
                <TooltipIconButton title="Add new" onClick={() => setOpenAddDialog('plate list')}>
                  <AddIcon />
                </TooltipIconButton>
              )}
              {isUserRoot && (
                <TooltipIconButton
                  title="Import"
                  onClick={async () => {
                    const files = await selectFiles();
                    for (const file of files) await processUploadedPlateListFile(file);
                  }}
                >
                  <Upload />
                </TooltipIconButton>
              )}
            </TypographyWithActionButtons>

            {sortedPlateLists.map((list) => (
              <PlateListCard key={list.guid} value={list} />
            ))}
          </Grid>
        )}
        <Grid size={{ xs: 12, md: canUsePlates ? 4 : 6 }}>
          <TypographyWithActionButtons text="Hanger Lists" variant="h4" gutterBottom>
            {hasEditPermission && (
              <TooltipIconButton title="Add new" onClick={() => setOpenAddDialog('hanger list')}>
                <AddIcon />
              </TooltipIconButton>
            )}
            {isUserRoot && (
              <TooltipIconButton
                title="Import"
                onClick={async () => {
                  const files = await selectFiles();
                  for (const file of files) await processUploadedHangerListFile(file);
                }}
              >
                <Upload />
              </TooltipIconButton>
            )}
          </TypographyWithActionButtons>

          {sortedHangerLists.map((list) => (
            <HangerListCard key={list.guid} value={list} />
          ))}
        </Grid>
      </Grid>

      {openAddDialog && (
        <NameDialog
          open
          noun={openAddDialog}
          verb="create"
          defaultValue=""
          onClose={() => setOpenAddDialog(null)}
          onSubmit={async (name) => {
            switch (openAddDialog) {
              case 'plate list':
                const newPlateListGuid = await dispatch(createPlateList(name));
                navigate(`/plateLists/${newPlateListGuid}`);
                break;

              case 'lumber list':
                const newLumberListGuid = await dispatch(createLumberListPrototype(name));
                navigate(`/lumberLists/${newLumberListGuid}`);
                break;

              case 'hanger list':
                const newHangerListGuid = await dispatch(createHangerList(name));
                navigate(`/hangerLists/${newHangerListGuid}`);
                break;

              default:
                throw new Error('Add dialog on main page was submitted when it was not open.');
            }
          }}
        />
      )}
      {renderFileSelect()}
    </Container>
  );
}
